import React, { useMemo } from "react";

/**
 * Simple, dependency-free SVG line chart.
 * Renders period-by-period actuals as a line with dots, plus a horizontal target line
 * and a shaded "success" band. Direction "up" makes the band above the target;
 * direction "down" makes it below.
 */
export default function PerformanceChart({ periods = [], measurements = [], target = 0, unit = "", direction = "up" }) {
  const data = useMemo(() => {
    const map = Object.fromEntries((measurements || []).map((m) => [m.period, m.value]));
    return periods.map((p) => ({ period: p, value: map[p] ?? null }));
  }, [periods, measurements]);

  const values = data.map((d) => d.value).filter((v) => v !== null && v !== undefined);
  const min = Math.min(target || 0, ...values, 0);
  const max = Math.max(target || 0, ...values, 1);
  const range = Math.max(max - min, 1);
  // Add 15% headroom on top and bottom
  const yMin = min - range * 0.15;
  const yMax = max + range * 0.15;

  const W = 720;
  const H = 240;
  const pad = { l: 40, r: 16, t: 12, b: 26 };
  const chartW = W - pad.l - pad.r;
  const chartH = H - pad.t - pad.b;

  const xFor = (i) => pad.l + (chartW * i) / Math.max(periods.length - 1, 1);
  const yFor = (v) => pad.t + chartH - ((v - yMin) / (yMax - yMin)) * chartH;
  const targetY = yFor(target);

  // Line path segments — break the line on null values
  let path = "";
  data.forEach((d, i) => {
    if (d.value == null) return;
    const x = xFor(i);
    const y = yFor(d.value);
    // If previous was null, use M; otherwise L
    const prev = data[i - 1];
    path += `${prev && prev.value != null ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)} `;
  });

  // Success band
  const bandY1 = direction === "up" ? pad.t : targetY;
  const bandY2 = direction === "up" ? targetY : pad.t + chartH;

  // Y-axis ticks (5)
  const ticks = Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) * i) / 4);

  return (
    <div data-testid="objective-chart" className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#2563EB" />
          </linearGradient>
        </defs>
        {/* Success band */}
        <rect
          x={pad.l}
          y={Math.min(bandY1, bandY2)}
          width={chartW}
          height={Math.abs(bandY2 - bandY1)}
          fill="#10B981"
          fillOpacity="0.08"
        />
        {/* Y grid + ticks */}
        {ticks.map((t, i) => {
          const y = yFor(t);
          return (
            <g key={i}>
              <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="#E2E8F0" strokeDasharray="2 4" />
              <text x={pad.l - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#94A3B8" fontFamily="sans-serif">
                {Number(t).toFixed(t < 10 ? 1 : 0)}
              </text>
            </g>
          );
        })}
        {/* Target line */}
        <line
          x1={pad.l}
          y1={targetY}
          x2={W - pad.r}
          y2={targetY}
          stroke="#10B981"
          strokeWidth="1.5"
          strokeDasharray="6 4"
        />
        <text x={W - pad.r} y={targetY - 4} textAnchor="end" fontSize="9" fontWeight="700" fill="#059669" fontFamily="sans-serif">
          Target: {target}{unit ? ` ${unit}` : ""}
        </text>
        {/* Actual line */}
        <path d={path} fill="none" stroke="url(#line-grad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Dots + labels */}
        {data.map((d, i) => {
          if (d.value == null) return null;
          const cx = xFor(i);
          const cy = yFor(d.value);
          const met = direction === "up" ? d.value >= target : d.value <= target;
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r="4.5" fill={met ? "#10B981" : "#F43F5E"} stroke="white" strokeWidth="1.5" />
              <text x={cx} y={cy - 10} textAnchor="middle" fontSize="9" fill="#0F172A" fontWeight="700" fontFamily="sans-serif">
                {d.value}
              </text>
            </g>
          );
        })}
        {/* X labels */}
        {data.map((d, i) => (
          <text
            key={i}
            x={xFor(i)}
            y={H - 8}
            textAnchor="middle"
            fontSize="9"
            fill="#64748B"
            fontFamily="sans-serif"
            fontWeight={d.value != null ? 600 : 400}
          >
            {d.period}
          </text>
        ))}
      </svg>
    </div>
  );
}
